import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import "./setFundPassword.css";

const METHOD_ORDER = ["totp", "email", "phone"];

const METHOD_LABELS = {
  totp: "Authenticator app",
  email: "Email",
  phone: "Mobile",
};

/**
 * Set Fund Password — standalone page.
 * Route: /user_profile/security/setFundPassword
 * Loads enabled verification methods from GET /v1/security/get-security-methods-list (passkey excluded).
 */
const SetFundPassword = () => {
  const navigate = useNavigate();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = profileCtx.userDetails || {};
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  /** "email" | "phone" | "totp" — matches `securityAddFundPassword` `type` (no passkey). */
  const [verifyType, setVerifyType] = useState("email");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [availableMethods, setAvailableMethods] = useState({
    email: false,
    phone: false,
    totp: false,
  });

  const maskedEmail = useMemo(() => {
    const email = String(userDetails?.emailId || userDetails?.email || "").trim();
    if (!email || !email.includes("@")) return "your email";
    const [name, domain] = email.split("@");
    return `${name.slice(0, 2)}***@${domain}`;
  }, [userDetails]);

  const enabledMethodKeys = useMemo(() => METHOD_ORDER.filter((k) => availableMethods[k]), [availableMethods]);
  const hasAnyVerificationMethod = enabledMethodKeys.length > 0;

  const fetchSecurityMethods = useCallback(async () => {
    setMethodsLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      const norm =
        typeof profileCtx.fetchSecurityMethods === "function"
          ? await profileCtx.fetchSecurityMethods()
          : null;

      if (!norm) {
        const result = await AuthService.getSecurityMethodsList();
        const m =
          result?.data?.security_methods ||
          result?.data?.data?.security_methods ||
          result?.security_methods ||
          result?.data?.securityMethods ||
          null;
        if (!result?.success || !m || typeof m !== "object") {
          alertErrorMessage(result?.message || "Could not load verification methods.");
          setAvailableMethods({ email: false, phone: false, totp: false });
          return;
        }
        const methodsObj = {
          email: !!m.email,
          phone: !!(m.mobile ?? m.phone ?? m.sms),
          totp: !!m.totp,
        };
        setAvailableMethods(methodsObj);

        const first = METHOD_ORDER.find((k) => methodsObj[k]);
        if (first) {
          setVerifyType(first);
        } else {
          alertErrorMessage("No verification method available. Enable email, mobile, or authenticator in Security first.");
        }
        return;
      }

      const methodsObj = {
        email: !!norm.email,
        phone: !!norm.phone,
        totp: !!norm.totp,
      };
      setAvailableMethods(methodsObj);

      const first = METHOD_ORDER.find((k) => methodsObj[k]);
      if (first) {
        setVerifyType(first);
      } else {
        alertErrorMessage("No verification method available. Enable email, mobile, or authenticator in Security first.");
      }
    } catch (e) {
      alertErrorMessage(e?.message || "Could not load verification methods.");
      setAvailableMethods({ email: false, phone: false, totp: false });
    } finally {
      setMethodsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [profileCtx]);

  useEffect(() => {
    void fetchSecurityMethods();
  }, [fetchSecurityMethods]);

  useEffect(() => {
    setVerifyCode("");
  }, [verifyType]);

  const isConfirmEnabled = useMemo(() => {
    if (!hasAnyVerificationMethod) return false;
    if (!newPwd.trim() || !confirmPwd.trim()) return false;
    if (!verifyCode.trim()) return false;
    return newPwd === confirmPwd;
  }, [newPwd, confirmPwd, verifyCode, hasAnyVerificationMethod]);

  const handleSend = async () => {
    if (!(verifyType === "email" || verifyType === "phone")) return;
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const target = verifyType === "phone" ? "mobile" : "email";
      const result = await AuthService.securitySendOtp(target, "fund_password");
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
      } else {
        alertErrorMessage(result?.message || "Failed to send verification code");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  return (
    <main className="sfp-page" aria-labelledby="sfp-title">
      <nav className="sfp-page__crumbs" aria-label="Breadcrumb">
        <ol className="sfp-page__crumbList">
          <li className="sfp-page__crumbItem">
            <button type="button" className="sfp-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="sfp-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="sfp-page__crumbItem sfp-page__crumbItem--active" aria-current="page">
            Set Fund Password
          </li>
        </ol>
      </nav>

      <div className="set_fundform_outer">
        <h1 id="sfp-title" className="sfp-page__title">
          Set Fund Password
        </h1>

        <form
          className="sfp-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!isConfirmEnabled) return;
            const submitCode = verifyCode.trim();
            try {
              setIsLoading(true);
              LoaderHelper.loaderStatus(true);
              const result = await AuthService.securityAddFundPassword(
                newPwd,
                confirmPwd,
                submitCode,
                verifyType
              );
              if (!result?.success) {
                alertErrorMessage(result?.message || "Failed to set fund password");
                return;
              }
              alertSuccessMessage(result?.message || "Fund password set successfully");
            } catch (error) {
              alertErrorMessage(error?.message || "Something went wrong");
              return;
            } finally {
              setIsLoading(false);
              LoaderHelper.loaderStatus(false);
            }
            navigate("/user_profile/security", { replace: true });
          }}
        >
          <div className="sfp-field">
            <label className="sfp-label" htmlFor="sfp-new">
              New Fund Password
            </label>
            <div className="sfp-inputWrap">
              <input
                id="sfp-new"
                className="sfp-input"
                type={showNew ? "text" : "password"}
                placeholder="Please Enter"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="sfp-togglePw"
                aria-label={showNew ? "Hide password" : "Show password"}
                onClick={() => setShowNew((v) => !v)}
              >
                <i className={showNew ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
              </button>
            </div>
            <p className="sfp-hint">Choose a strong fund password and keep it private.</p>
          </div>

          <div className="sfp-field">
            <label className="sfp-label" htmlFor="sfp-confirm">
              Confirm Fund Password
            </label>
            <div className="sfp-inputWrap">
              <input
                id="sfp-confirm"
                className="sfp-input"
                type={showConfirm ? "text" : "password"}
                placeholder="Please Enter"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="sfp-togglePw"
                aria-label={showConfirm ? "Hide password" : "Show password"}
                onClick={() => setShowConfirm((v) => !v)}
              >
                <i className={showConfirm ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
              </button>
            </div>
          </div>

          {methodsLoading ? (
            <p className="sfp-hint" role="status">
              Loading verification options…
            </p>
          ) : !hasAnyVerificationMethod ? (
            <p className="sfp-hint sfp-hint--warn" role="alert">
              No verification method is enabled. Add email, mobile, or authenticator in Security, then try again.
            </p>
          ) : (
            <div className="sfp-field">
              <label className="sfp-label" htmlFor="sfp-verify-code" style={{ marginTop: 12 }}>
                {verifyType === "totp"
                  ? "Google Authenticator Code"
                  : verifyType === "phone"
                    ? "Mobile Code"
                    : "Email Code"}
              </label>
              <div className="sfp-inputWrap">
                <input
                  id="sfp-verify-code"
                  className="sfp-input sfp-input--code"
                  placeholder="Please Enter"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  disabled={!hasAnyVerificationMethod}
                />
                {verifyType === "email" || verifyType === "phone" ? (
                  <button
                    type="button"
                    className="sfp-send"
                    onClick={handleSend}
                    disabled={isLoading || !hasAnyVerificationMethod}
                  >
                    Send
                  </button>
                ) : null}
              </div>
              {verifyType === "email" || verifyType === "phone" ? (
                <p className="sfp-hint">
                  Send the verification code to {verifyType === "email" ? maskedEmail : "your mobile"}, and the code will be valid for 10 minutes
                </p>
              ) : (
                <p className="sfp-hint">Enter the 6-digit code from your authenticator app.</p>
              )}
            </div>
          )}

          <button
            type="submit"
            className={`sfp-confirm ${isConfirmEnabled ? "is-enabled" : ""}`}
            disabled={!isConfirmEnabled || isLoading || methodsLoading || !hasAnyVerificationMethod}
          >
            Confirm
          </button>
        </form>
      </div>
    </main>
  );
};

export default SetFundPassword;
